import React, { useState, useEffect, useRef } from 'react';
import { Save, Check, RotateCcw, Eye, Mail } from 'lucide-react';
import { emailTemplatesApi } from '../../services/api';
import '../../pages/Pages.css';

interface EmailTemplate {
  id: string;
  template_key: string;
  label: string;
  subject: string;
  body: string;
  is_custom: boolean;
  placeholders: string[];
}

export default function EmailTemplateEditor() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await emailTemplatesApi.list();
      setTemplates(res.data);
      if (res.data.length > 0 && !selectedKey) {
        selectTemplate(res.data[0]);
      }
    } catch {
      setError('Vorlagen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (tpl: EmailTemplate) => {
    setSelectedKey(tpl.template_key);
    setSubject(tpl.subject);
    setBody(tpl.body);
    setIsCustom(tpl.is_custom);
    setPlaceholders(tpl.placeholders);
    setPreviewHtml('');
    setSaved(false);
    setError('');
    setHasChanges(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const res = await emailTemplatesApi.update(selectedKey, { subject, body });
      setIsCustom(res.data.is_custom);
      setTemplates(prev => prev.map(t =>
        t.template_key === selectedKey ? { ...t, subject, body, is_custom: true } : t
      ));
      setSaved(true);
      setHasChanges(false);
    } catch {
      setError('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Vorlage auf Standard zurücksetzen? Ihre Anpassungen gehen verloren.')) return;
    try {
      const res = await emailTemplatesApi.reset(selectedKey);
      setSubject(res.data.subject);
      setBody(res.data.body);
      setIsCustom(false);
      setTemplates(prev => prev.map(t =>
        t.template_key === selectedKey ? { ...t, subject: res.data.subject, body: res.data.body, is_custom: false } : t
      ));
      setPreviewHtml('');
      setSaved(false);
      setHasChanges(false);
    } catch {
      setError('Fehler beim Zurücksetzen');
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      // Save first if there are changes, so preview reflects current edits
      if (hasChanges) {
        await emailTemplatesApi.update(selectedKey, { subject, body });
        setTemplates(prev => prev.map(t =>
          t.template_key === selectedKey ? { ...t, subject, body, is_custom: true } : t
        ));
        setIsCustom(true);
        setHasChanges(false);
      }
      const res = await emailTemplatesApi.preview(selectedKey);
      setPreviewHtml(res.data.html);
    } catch {
      setError('Vorschau konnte nicht geladen werden');
    } finally {
      setPreviewLoading(false);
    }
  };

  const insertPlaceholder = (placeholder: string) => {
    const textarea = bodyRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = `{{${placeholder}}}`;
    const newBody = body.substring(0, start) + text + body.substring(end);
    setBody(newBody);
    setHasChanges(true);
    // Restore cursor position after the inserted text
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  };

  if (loading) {
    return <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Lade Vorlagen...</div>;
  }

  const currentTemplate = templates.find(t => t.template_key === selectedKey);

  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
      {/* Left Column — Template List + Editor */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Template List */}
        <div className="card" style={{ padding: 0, marginBottom: 16 }}>
          <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #e8e0d5' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>
              <Mail size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              E-Mail-Vorlagen
            </h3>
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {templates.map(tpl => (
              <button
                key={tpl.template_key}
                onClick={() => selectTemplate(tpl)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: '1px solid #f0ece6',
                  background: tpl.template_key === selectedKey ? 'var(--bg)' : 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  color: 'var(--text)',
                  fontWeight: tpl.template_key === selectedKey ? 600 : 400,
                  transition: 'background 0.15s',
                }}
              >
                <span>{tpl.label}</span>
                {tpl.is_custom && (
                  <span style={{
                    marginLeft: 8,
                    fontSize: '0.7rem',
                    background: 'var(--primary, #8B7355)',
                    color: '#fff',
                    padding: '2px 6px',
                    borderRadius: 4,
                  }}>Angepasst</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Editor */}
        {currentTemplate && (
          <div className="card" style={{ padding: 24 }}>
            {error && <div className="auth-error mb-16">{error}</div>}
            {saved && (
              <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '10px 16px', borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem' }}>
                <Check size={16} /> Gespeichert
              </div>
            )}

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Betreff</label>
              <input
                value={subject}
                onChange={(e) => { setSubject(e.target.value); setHasChanges(true); setSaved(false); }}
                placeholder="E-Mail-Betreff"
                style={{ fontSize: '0.9rem' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Inhalt</label>
              <textarea
                ref={bodyRef}
                value={body}
                onChange={(e) => { setBody(e.target.value); setHasChanges(true); setSaved(false); }}
                rows={14}
                placeholder="E-Mail-Text..."
                style={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 1.6 }}
              />
            </div>

            {/* Placeholder Chips */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8, display: 'block' }}>
                Platzhalter (Klick zum Einfügen)
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {placeholders.map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => insertPlaceholder(p)}
                    style={{
                      padding: '4px 10px',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      background: 'var(--bg)',
                      border: '1px solid #e8e0d5',
                      borderRadius: 4,
                      cursor: 'pointer',
                      color: 'var(--primary-dark, #5D4E37)',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'var(--primary, #8B7355)';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'var(--bg)';
                      e.currentTarget.style.color = 'var(--primary-dark, #5D4E37)';
                    }}
                  >
                    {`{{${p}}}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !hasChanges}>
                <Save size={16} /> {saving ? 'Speichern...' : 'Speichern'}
              </button>
              <button className="btn btn-secondary" onClick={handlePreview} disabled={previewLoading}>
                <Eye size={16} /> {previewLoading ? 'Lade...' : 'Vorschau'}
              </button>
              {isCustom && (
                <button className="btn btn-secondary" onClick={handleReset}>
                  <RotateCcw size={16} /> Standard wiederherstellen
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Right Column — Preview */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #e8e0d5' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>
              <Eye size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Vorschau
            </h3>
          </div>
          {previewHtml ? (
            <div style={{ padding: 0 }}>
              <iframe
                srcDoc={previewHtml}
                style={{
                  width: '100%',
                  height: 600,
                  border: 'none',
                  background: 'var(--bg)',
                }}
                title="E-Mail-Vorschau"
                sandbox="allow-same-origin"
              />
            </div>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#aaa', fontSize: '0.9rem' }}>
              <Eye size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
              <p>Klicke auf "Vorschau", um die gerenderte E-Mail zu sehen.</p>
              <p style={{ fontSize: '0.8rem' }}>Platzhalter werden durch Beispielwerte ersetzt.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
